import React, { useEffect, useState } from 'react';
import { addressAPI } from '../../services/api';
import { Loader, Plus, MapPin, Edit, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const AddressBook = () => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        mobile: '',
        address: '',
        village: '',
        city: '',
        district: '',
        state: '',
        pincode: ''
    });

    useEffect(() => {
        fetchAddresses();
    }, []);

    const fetchAddresses = async () => {
        try {
            const response = await addressAPI.getAll();
            setAddresses(response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch addresses", error);
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this address?")) {
            try {
                await addressAPI.delete(id);
                fetchAddresses();
            } catch (error) {
                console.error("Failed to delete address", error);
                alert("Failed to delete address");
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await addressAPI.create({ ...formData, country: 'India' });
            setIsFormOpen(false);
            setFormData({
                name: '',
                mobile: '',
                address: '',
                village: '',
                city: '',
                district: '',
                state: '',
                pincode: ''
            });
            fetchAddresses();
        } catch (error) {
            console.error("Failed to create address", error);
            alert("Failed to save address");
        }
    };

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12">
            <div className="container mx-auto max-w-4xl">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Address Book</h1>
                    <button
                        onClick={() => setIsFormOpen(true)}
                        className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition"
                    >
                        <Plus size={20} /> Add New Address
                    </button>
                </div>

                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <Loader className="animate-spin text-green-600" size={48} />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {addresses.map((addr, index) => (
                            <div key={addr.id || index} className="bg-white p-6 rounded-xl shadow-md border border-gray-100 relative">
                                <div className="flex items-start gap-4">
                                    <div className="bg-green-50 p-3 rounded-full text-green-600">
                                        <MapPin size={24} />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="font-semibold text-gray-900">{addr.name}</h3>
                                        <p className="text-gray-600 text-sm mt-1">{addr.address}</p>
                                        <p className="text-gray-600 text-sm">{addr.village}, {addr.city}</p>
                                        <p className="text-gray-600 text-sm">{addr.district}, {addr.state} - {addr.pincode}</p>
                                        <p className="text-gray-800 font-medium text-sm mt-2">Phone: {addr.mobile}</p>
                                    </div>
                                    <button
                                        onClick={() => handleDelete(addr.id)}
                                        className="text-red-500 hover:text-red-700 p-2"
                                        title="Delete Address"
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </div>
                            </div>
                        ))}
                        {addresses.length === 0 && !loading && (
                            <div className="col-span-full text-center py-12 text-gray-500">
                                No addresses found. Add one to get started.
                            </div>
                        )}
                    </div>
                )}

                {/* Add Address Modal */}
                {isFormOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800">Add New Address</h2>
                                <button onClick={() => setIsFormOpen(false)} className="text-gray-400 hover:text-gray-600">
                                    &times;
                                </button>
                            </div>
                            <form onSubmit={handleSubmit} className="p-6 space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input required name="name" placeholder="Full Name" value={formData.name} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                    <input required name="mobile" placeholder="Mobile Number" value={formData.mobile} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                </div>
                                <textarea required name="address" placeholder="Address (House No, Street Area)" rows="3" value={formData.address} onChange={handleInputChange} className="w-full p-3 border rounded-lg"></textarea>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input required name="village" placeholder="Village" value={formData.village} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                    <input required name="city" placeholder="City" value={formData.city} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <input required name="district" placeholder="District" value={formData.district} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                    <input required name="state" placeholder="State" value={formData.state} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                    <input required name="pincode" placeholder="Pincode" value={formData.pincode} onChange={handleInputChange} className="w-full p-3 border rounded-lg" />
                                </div>
                                <div className="pt-4 flex justify-end gap-3">
                                    <button type="button" onClick={() => setIsFormOpen(false)} className="px-6 py-2 border rounded-lg hover:bg-gray-50">Cancel</button>
                                    <button type="submit" className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Save Address</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddressBook;
